import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "onChange", "value", "onBlur", "phoneNumberType", "setIsPhoneNumberEmpty"];
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
// @ts-ignore Untyped component
import PhoneNumberInput from 'ui-addon-i18n/components/PhoneNumberInput';
// @ts-ignore Untyped module
import { allCountries, iso2Lookup } from 'I18n/constants/TelephoneData';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { useValidationErrors } from '../../hooks/useValidationErrors';
import { INVALID_PHONE_NUMBERS } from '../../constants/ValidationErrors';
import { setErrorMessages, getShowAllErrors } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';

// PhoneNumberInput (from ui-addon-i18n) renders an outer View with display="inline-flex"
// hardcoded (cannot be overridden). Its child InputGroup → Fieldset is a flex item with
// no flex-grow, so it only fills content width instead of the full parent. These CSS rules
// make the fieldset fill the View, and the text input's FormControl fill the InputGroupWrapper.
// Note: Trellis components block className/style via filterProps, so we need this wrapper div
// to inject the CSS rather than attaching it to FormControl or PhoneNumberInput directly.
import { jsx as _jsx } from "react/jsx-runtime";
const PhoneNumberInputFullWidth = styled.div.withConfig({
  displayName: "CustomFormFieldPhone__PhoneNumberInputFullWidth"
})(["& fieldset{flex:1;min-width:0;}& fieldset > div > div:last-child > div:first-child >:not(:first-child){flex:1 1 0%;min-width:0;}"]);
// NOTE: this extra component is necessary to allow us to pass the accessibility
// props from UIFormControl to the phoneInputProps
const PhoneNumberInputWrapper = _ref => {
  let {
      disabled,
      onChange,
      value = '',
      onBlur,
      phoneNumberType,
      setIsPhoneNumberEmpty
    } = _ref,
    formControlAccessibilityProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    dispatch,
    formState
  } = usePaymentFormContext();
  const phoneNumbers = formState.get('phoneNumbers').toJS();
  return /*#__PURE__*/_jsx(PhoneNumberInputFullWidth, {
    children: /*#__PURE__*/_jsx(PhoneNumberInput, {
      countrySelectProps: {
        buttonUse: 'form-on-dark'
      },
      "data-test-id": "custom-phone-number-form-field-input",
      disabled: disabled,
      inlineSize: "100%",
      phoneInputProps: Object.assign({
        flex: 1,
        inlineSize: '100%',
        use: 'on-dark'
      }, formControlAccessibilityProps),
      onChange: phoneNumberEvent => {
        // phone number always includes country's dial code even if empty,
        // so we have to check it's not actually empty before validating
        if (phoneNumberEvent.phoneNumber !== `+${allCountries[iso2Lookup[phoneNumberEvent.country]].dialCode}`) {
          setIsPhoneNumberEmpty(false);
          dispatch({
            type: 'setPhoneNumbers',
            data: Object.assign({}, phoneNumbers, {
              [phoneNumberType]: phoneNumberEvent.phoneNumber
            })
          });
          onChange(syntheticEvent(phoneNumberEvent.phoneNumber));
        } else {
          setIsPhoneNumberEmpty(true);
          // if empty, we don't want to validate
          const phoneNumbersWithoutCurrent = Object.fromEntries(Object.entries(phoneNumbers).filter(([key]) => key !== phoneNumberType));
          dispatch({
            type: 'setPhoneNumbers',
            data: Object.assign({}, phoneNumbersWithoutCurrent)
          });
          // propagate empty string so customData[fieldName] is falsy,
          // ensuring getIsCustomFormFieldsValid correctly blocks submission
          onChange(syntheticEvent(''));
        }
      },
      value: value,
      onBlur: onBlur
    })
  });
};
const CustomFormFieldPhone = ({
  disabled,
  formField,
  onChange,
  value
}) => {
  const reduxDispatch = useDispatch();
  const {
    formFieldValidationErrors,
    flushDebouncedGetValidationErrors
  } = useValidationErrors();
  const showAllErrors = useSelector(getShowAllErrors);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const fieldName = formField.name;
  const fieldLabel = formField.label;
  const [isPhoneNumberEmpty, setIsPhoneNumberEmpty] = useState(!value);
  const isEmptyError = formField.required && (hasBeenBlurred || showAllErrors) && isPhoneNumberEmpty;
  const isInvalidError = formFieldValidationErrors.some(error => error.subCategory === INVALID_PHONE_NUMBERS && Object.prototype.hasOwnProperty.call(error.context, fieldName));
  const isError = isEmptyError || isInvalidError;
  const errorKey = isEmptyError ? `empty${fieldLabel}` : `invalidPhoneNumber_${fieldLabel}`;
  const errorMessage = useGetErrorMessagesAndTranslations().get(errorKey);
  useEffect(() => {
    reduxDispatch(setErrorMessages({
      [`empty${fieldLabel}`]: {
        isError: isEmptyError,
        customFormFieldLabel: fieldLabel
      },
      [`invalidPhoneNumber_${fieldLabel}`]: {
        isError: isInvalidError,
        customFormFieldLabel: fieldLabel
      }
    }));
  }, [isEmptyError, isInvalidError, reduxDispatch, fieldLabel]);
  const handleBlur = useCallback(() => {
    setHasBeenBlurred(true);
    flushDebouncedGetValidationErrors();
  }, [flushDebouncedGetValidationErrors]);
  return /*#__PURE__*/_jsx(FormControl, {
    help: formField.description,
    label: fieldLabel,
    required: formField.required,
    error: isError,
    validationMessage: isError && errorMessage,
    paddingBlockStart: "100",
    children: /*#__PURE__*/_jsx(PhoneNumberInputWrapper, {
      disabled: disabled,
      onChange: onChange,
      phoneNumberType: fieldName,
      value: value,
      onBlur: handleBlur,
      setIsPhoneNumberEmpty: setIsPhoneNumberEmpty
    })
  });
};
export default CustomFormFieldPhone;